import React, { useState } from 'react';
import { ContextMenu, ContextMenuButton } from '../../../_lib/components/ContextMenu';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { SharedFilesItemDeleteForm } from '../forms/SharedFilesItemDeleteForm';
import { SharedFilesItemMoveForm } from '../forms/SharedFilesItemMoveForm';
import { SharedFilesItemEditForm } from '../forms/SharedFilesItemEditForm';
import { SharedFilesInformation } from './SharedFilesInformation';

import type { SharedFile } from '../SharedFiles.types';
import { getDisplayNameByFileItem } from '../../../types/api/FileItem';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';

/**
 * all possible Actions of a single File.
 * Keep the file in state to avoid rerendering with new info from WS messages.
 */
type ActionState = {
  type: 'delete' | 'move' | 'edit' | 'information';
  file: SharedFile;
};

/**
 * Shared files actions of a single file.
 */
export function SharedFilesItemAction({ file }: { file: SharedFile }) {
  const { t } = useSonTranslation('SharedFiles');

  const [currentAction, setAction] = useState<ActionState>();
  const closeModal = () => setAction(undefined);
  const openAction = (type: ActionState['type']) => () => setAction({ type, file });

  const { forms } = file;
  const fileHasNoAction = !forms || Object.keys(forms).length === 0;

  if (fileHasNoAction) {
    return null;
  }

  const switchForm = () => {
    switch (currentAction?.type) {
      case 'information':
        return <SharedFilesInformation closeModal={closeModal} file={currentAction.file} />;
      case 'delete':
        return <SharedFilesItemDeleteForm closeModal={closeModal} file={currentAction.file} />;
      case 'move':
        return <SharedFilesItemMoveForm closeModal={closeModal} file={currentAction.file} />;
      case 'edit':
        return <SharedFilesItemEditForm closeModal={closeModal} file={currentAction.file} />;
      default:
        return null;
    }
  };

  return (
    <>
      <ContextMenu
        tooltipText={t('actions.manage', { name: getDisplayNameByFileItem(file) })}
        tooltipPlacement={TooltipPlacement.TOP}
      >
        <ContextMenuButton onClick={openAction('information')}>{t('actions.information')}</ContextMenuButton>
        {forms.edit && <ContextMenuButton onClick={openAction('edit')}>{t('actions.edit')}</ContextMenuButton>}
        {forms.delete && <ContextMenuButton onClick={openAction('delete')}>{t('actions.delete')}</ContextMenuButton>}
        {forms.move && <ContextMenuButton onClick={openAction('move')}>{t('actions.move')}</ContextMenuButton>}
      </ContextMenu>
      {switchForm()}
    </>
  );
}
